import { Command, CommandItem } from "@comps/ui/command";
import { Label } from "@comps/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { TreeDataItem, TreeView } from "@comps/ui/tree-view";
import { LineChartData, LineChartWidgetType, LineStyle, Widget } from "@dto/Dashboard";
import Field, { AggregationType, validAggregationByType, validNumericAggregationByType } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { Filter, Path } from "@dto/records";
import { ASC, DESC, NONE, SortType } from "@dto/views";
import { fieldToPath, pathKey, pathKeyToPath } from "@utils/model";
import { buildFieldTree, buildPathTree, getTreePathValue } from "@utils/tree";
import { TW_DashboardCardFieldArrow, TW_DashboardCardFieldSelected, TW_DashboardCardFieldText, TW_DashboardCardTreeView, TW_DashboardPathSelector, TW_DashboardSection_Arrow, TW_DashboardSection_Label, TW_DashboardSection_ScrollArea, TW_SharedObjectsCollapsibleTrigger, TW_WidgetSectionLabel, TW_WidgetSpecificSection } from "@utils/twClasses";
import { getDefaultAggregation } from "@utils/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";
import { WidgetCardConfigProps } from "./WidgetConfig";
import { Switch } from "@comps/ui/switch";
import { Separator } from "@comps/ui/separator";
import { RadioGroup, RadioGroupItem } from "@comps/ui/radio";
import WidgetFilterEditor from "./WidgetFilterEditor";
import { GRAY_300_COLOR } from "@utils/colors";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@comps/ui/collapsible";
import { ScrollArea } from "@comps/ui/scroll";


export function LineChartCardConfig({ initialWidget, onConfigChange }: WidgetCardConfigProps) {

    const objectInfos = useModelStore(state => state.objectInfos);
    const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
    const workspaceByKeys = useModelStore(state => state.workspaceByKeys);
    const [selectedObject, setSelectedObject] = useState<ObjectInfos | undefined>(undefined);
    const [selectedField, setSelectedField] = useState<Field | undefined>(undefined);
    const [selectedFieldAggregation, setSelectedFieldAggregation] = useState<AggregationType | undefined>(undefined);
    const [selectedSplitByPath, setSelectedSplitByPath] = useState<Path | undefined>(undefined);
    const [selectedSplitByName, setSelectedSplitByName] = useState<string | undefined>(undefined);
    const [selectedSplitByAggregation, setSelectedSplitByAggregation] = useState<AggregationType | undefined>(undefined);
    const [selectedSplitBySortType, setSelectedSplitBySortType] = useState<SortType>(NONE);
    const [selectedCategoryPath, setSelectedCategoryPath] = useState<Path | undefined>(undefined);
    const [selectedCategoryName, setSelectedCategoryName] = useState<string | undefined>(undefined);
    const [selectedCategoryAggregation, setSelectedCategoryAggregation] = useState<AggregationType | undefined>(undefined);
    const [selectedCategorySortType, setSelectedCategorySortType] = useState<SortType>(NONE);
    const [selectedFilter, setSelectedFilter] = useState<Filter | null>(null);
    const [selectedShowDots, setSelectedShowDots] = useState<boolean>(false);
    const [selectedNullsAsZeros, setSelectedNullsAsZeros] = useState<boolean>(false);
    const [selectedCategoryByYear, setSelectedCategoryByYear] = useState<boolean>(false);
    const [selectedShowEmptyCategories, setSelectedShowEmptyCategories] = useState<boolean>(false);
    const [selectedLineStyle, setSelectedLineStyle] = useState<LineStyle>(LineStyle.MONOTONE);
    const [fieldAggregationByPathKeys, setFieldAggregationByPathKeys] = useState<Map<string, AggregationType | undefined>>(new Map<string, AggregationType | undefined>());
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const objectByKeys = useModelStore(state => state.objectByKeys);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const wsColor = useModelStore(state => state.wsColor);
    const { t } = useTranslation();
    const [isSharedObjectsOpen, setIsSharedObjectsOpen] = useState(false);


    useEffect(() => {
        if (initialWidget) {
            if (initialWidget?.data) {
                const data = initialWidget.data as LineChartData;
                if (data.valuePath) {
                    const path = pathKeyToPath(data.valuePath, relationByKeys);
                    if (path) {
                        setSelectedField(fieldByKeys?.get(path.field));
                        setSelectedObject(objectByKeys?.get(path.object));
                        setSelectedFieldAggregation(data.valueAggregation ?? undefined);
                    }
                }

                if (data.splitByPath) {
                    const path = pathKeyToPath(data.splitByPath, relationByKeys);
                    if (path) {
                        setSelectedSplitByPath(path);
                        const splitObject = objectByKeys?.get(path.object);
                        const splitField = splitObject?.fields?.find(field => field._key === path.field);
                        setSelectedSplitByName(splitObject?.object.name + " > " + splitField?.name);
                        setSelectedSplitByAggregation(data.splitByAggregation ?? undefined);
                        setSelectedSplitBySortType(data.splitBySortType ?? undefined);
                    }
                }
                if (data.categoryPath) {
                    const path = pathKeyToPath(data.categoryPath, relationByKeys);
                    if (path) {
                        setSelectedCategoryPath(path);
                        const categoryObject = objectByKeys?.get(path.object);
                        const categoryField = categoryObject?.fields?.find(field => field._key === path.field);
                        setSelectedCategoryName(categoryObject?.object.name + " > " + categoryField?.name);
                        setSelectedCategoryAggregation(data.categoryAggregation ?? undefined);
                        setSelectedCategorySortType(data.categorySortType ?? undefined);
                    }
                }
                setSelectedShowDots(data.showDots ?? false);
                setSelectedNullsAsZeros(data.nullsAsZeros ?? false);
                setSelectedCategoryByYear(data.categoryByYear ?? false);
                setSelectedShowEmptyCategories(data.showEmptyCategories ?? false);
                setSelectedLineStyle(data.lineStyle ?? LineStyle.MONOTONE);
                if (data.filter) {
                    setSelectedFilter(data.filter);
                }
            }
        }
    }, [initialWidget]);

    function updateFieldAggregationByPathKeys(fieldAggregation: AggregationType | undefined, splitByAggregation: AggregationType | undefined, categoryAggregation: AggregationType | undefined) {
        const newFieldAggregationByPathKeys = new Map<string, AggregationType | undefined>();

        if (fieldAggregation && selectedField?._key) {
            const valuePath = (selectedObject?.object?._key && selectedField) ? fieldToPath(selectedObject.object._key, relationByKeys, fieldByKeys, selectedField) : null;
            const valuePathKey = valuePath ? pathKey(valuePath) : null;
            if (valuePathKey) {
                newFieldAggregationByPathKeys.set(valuePathKey, fieldAggregation);
            }
        }
        if (splitByAggregation && selectedSplitByPath) {
            const splitByPathKey = selectedSplitByPath ? pathKey(selectedSplitByPath) : null;
            if (splitByPathKey) {
                newFieldAggregationByPathKeys.set(splitByPathKey, splitByAggregation);
            }
        }
        if (categoryAggregation && selectedCategoryPath) {
            const categoryPathKey = selectedCategoryPath ? pathKey(selectedCategoryPath) : null;
            if (categoryPathKey) {
                newFieldAggregationByPathKeys.set(categoryPathKey, categoryAggregation);
            }
        }
        setFieldAggregationByPathKeys(newFieldAggregationByPathKeys);
    }

    function getNewWidget(
        valueField: Field | undefined,
        valueAggregation: AggregationType | undefined,
        splitByPath: Path | undefined,
        splitByAggregation: AggregationType | undefined,
        splitBySortType: SortType | undefined,
        categoryPath: Path | undefined,
        categoryAggregation: AggregationType | undefined,
        categorySortType: SortType | undefined,
        showDots: boolean,
        nullsAsZeros: boolean,
        categoryByYear: boolean,
        lineStyle: LineStyle,
        showEmptyCategories: boolean,
        filter: Filter | null
    ): Widget | undefined {
        if (!valueField || !valueAggregation) {
            return undefined;
        }
        const valuePath = (selectedObject?.object?._key && valueField) ? fieldToPath(selectedObject.object._key, relationByKeys, fieldByKeys, valueField) : null;
        const valuePathKey = valuePath ? pathKey(valuePath) : null;
        const splitByPathKey = splitByPath ? pathKey(splitByPath) : null;
        const categoryPathKey = categoryPath ? pathKey(categoryPath) : null;
        const newWidget: Widget = {
            type: LineChartWidgetType,
            title: initialWidget?.title || "Line chart", // Overridden by WidgetConfig
            description: initialWidget?.description || "", // Overridden by WidgetConfig
            width: initialWidget?.width ?? null, // Overridden by WidgetConfig
            height: initialWidget?.height ?? null, // Overridden by WidgetConfig
            showLegend: initialWidget?.showLegend ?? true, // Overridden by WidgetConfig
            showValues: initialWidget?.showValues ?? true, // Overridden by WidgetConfig
            showPercentage: initialWidget?.showPercentage ?? false, // Overridden by WidgetConfig
            hideYAxis: initialWidget?.hideYAxis === true, // Overridden by WidgetConfig
            data: new LineChartData(valuePathKey, valueAggregation, splitByPathKey, splitByAggregation, splitBySortType, categoryByYear, categoryPathKey, categoryAggregation, categorySortType, lineStyle, showDots, nullsAsZeros, showEmptyCategories, filter)
        }
        return newWidget;
    }

    function onFieldChange(objectKey: string | undefined, fieldKey: string | undefined) {
        if (!objectKey || !fieldKey) {
            setSelectedField(undefined);
            setSelectedObject(undefined);
            setSelectedFieldAggregation(undefined);
            setSelectedSplitByPath(undefined);
            setSelectedSplitByName(undefined);
            setSelectedSplitByAggregation(undefined);
            setSelectedSplitBySortType(NONE);
            setSelectedCategoryPath(undefined);
            setSelectedCategoryName(undefined);
            setSelectedCategoryAggregation(undefined);
            setSelectedCategorySortType(NONE);
            updateFieldAggregationByPathKeys(undefined, undefined, undefined);
            onConfigChange(undefined);
        } else {
            const field = fieldByKeys?.get(fieldKey);
            const fieldAggregation = getDefaultAggregation(field);
            const newWidget = getNewWidget(field, fieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedField(field);
            let defaultAggr = undefined;
            const validAggrs = validNumericAggregationByType(field?.type, field?.typeItems);
            if (validAggrs.length > 0) {
                defaultAggr = validAggrs[0];
            }
            setSelectedFieldAggregation(defaultAggr);
            updateFieldAggregationByPathKeys(defaultAggr, selectedSplitByAggregation, selectedCategoryAggregation);
            setSelectedObject(objectByKeys?.get(objectKey));
        }
    }

    function onSplitByChange(splitById: string | undefined) {

        if (!objectByKeys || !relationByKeys) {
            setSelectedSplitByPath(undefined);
            setSelectedSplitByName(undefined);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, undefined, selectedCategoryAggregation);
            onConfigChange(undefined);
            return;
        }

        const treePathValue = getTreePathValue(splitById, objectByKeys, relationByKeys);
        if (treePathValue) {
            const splitField = fieldByKeys?.get(treePathValue.path.field);
            const splitAggregation = getDefaultAggregation(splitField);
            const newWidget = getNewWidget(selectedField, selectedFieldAggregation, treePathValue.path, splitAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedSplitByPath(treePathValue.path);
            setSelectedSplitByName(treePathValue.name);
            setSelectedSplitByAggregation(splitAggregation);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, splitAggregation, selectedCategoryAggregation);
        } else {
            setSelectedSplitByPath(undefined);
            setSelectedSplitByName(undefined);
            setSelectedSplitByAggregation(undefined);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, undefined, selectedCategoryAggregation);
            onConfigChange(undefined);
        }
    }

    function onCategoryChange(categoryId: string | undefined) {

        if (!objectByKeys || !relationByKeys) {
            setSelectedCategoryPath(undefined);
            setSelectedCategoryName(undefined);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, selectedSplitByAggregation, undefined);
            onConfigChange(undefined);
            return;
        }

        const treePathValue = getTreePathValue(categoryId, objectByKeys, relationByKeys);
        if (treePathValue) {
            const categoryField = fieldByKeys?.get(treePathValue.path.field);
            const categoryAggregation = getDefaultAggregation(categoryField);
            const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, treePathValue.path, categoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedCategoryAggregation(categoryAggregation);
            setSelectedCategoryPath(treePathValue.path);
            setSelectedCategoryName(treePathValue.name);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, selectedSplitByAggregation, categoryAggregation);
        } else {
            const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, undefined, undefined, undefined, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedCategoryPath(undefined);
            setSelectedCategoryName(undefined);
            setSelectedCategoryAggregation(undefined);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, selectedSplitByAggregation, undefined);
        }
    }


    const onFieldAggregationChange = (value: string | undefined) => {
        if (value) {
            const fieldAggregation = value as AggregationType;
            const newWidget = getNewWidget(selectedField, fieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedFieldAggregation(fieldAggregation);
            updateFieldAggregationByPathKeys(fieldAggregation, selectedSplitByAggregation, selectedCategoryAggregation);
        } else {
            setSelectedFieldAggregation(undefined);
            updateFieldAggregationByPathKeys(undefined, selectedSplitByAggregation, selectedCategoryAggregation);
            onConfigChange(undefined);
        }
    }

    const onCategoryAggregationChange = (value: string | undefined) => {
        const categoryAggregation = value ? value as AggregationType : undefined;
        if (categoryAggregation) {
            const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, selectedCategoryPath, categoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedCategoryAggregation(categoryAggregation);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, selectedSplitByAggregation, categoryAggregation);
        } else {
            setSelectedCategoryAggregation(undefined);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, selectedSplitByAggregation, undefined);
            // onConfigChange(undefined);
        }
    }


    const onSplitByAggregationChange = (value: string | undefined) => {
        const splitByAggregation = value ? value as AggregationType : undefined;
        if (splitByAggregation) {
            const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, splitByAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedSplitByAggregation(splitByAggregation);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, splitByAggregation, selectedCategoryAggregation);
        } else {
            setSelectedSplitByAggregation(undefined);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, undefined, selectedCategoryAggregation);
            onConfigChange(undefined);
        }
    }

    const onCategorySortTypeChange = (value: string | undefined) => {
        const sortType = value ? value as SortType : NONE;
        setSelectedCategorySortType(sortType);
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, sortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }

    const onSplitBySortTypeChange = (value: string | undefined) => {
        const sortType = value ? value as SortType : NONE;
        setSelectedSplitBySortType(sortType);
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, sortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }

    const onShowDotsChange = (value: boolean) => {
        setSelectedShowDots(value);
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, value, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }


    const onNullsAsZerosChange = (value: boolean) => {
        setSelectedNullsAsZeros(value);
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, value, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }

    const onCategoryByYearChange = (value: boolean) => {
        setSelectedCategoryByYear(value);
        let categoryPath = selectedCategoryPath;
        if (value) {
            categoryPath = undefined;
        }
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, categoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, value, selectedLineStyle, selectedShowEmptyCategories, selectedFilter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }

    const onLineStyleChange = (value: string | undefined) => {
        const lineStyleValue = value ? value as LineStyle : LineStyle.MONOTONE;
        setSelectedLineStyle(lineStyleValue);
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, lineStyleValue, selectedShowEmptyCategories, selectedFilter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }

    const valueFieldTree = buildFieldTree(objectInfos, onFieldChange);
    const sharedObjectTree: TreeDataItem[] = sharedObjectInfos?.map(ws => ({
        id: ws.wsKey,
        name: workspaceByKeys?.get(ws.wsKey)?.name ?? "",
        children: ws.objectInfos.map(obj => ({
            id: obj.object._key,
            name: obj.object.name,
            children: obj.fields?.map(field => ({
                id: field._key,
                name: field.name,
                onClick: () => onFieldChange(obj.object._key, field._key)
            }))
        })),
    }));

    const aggregationFieldOptions = validNumericAggregationByType(selectedField?.type, selectedField?.typeItems);

    const splitByField = selectedSplitByPath?.field ? fieldByKeys?.get(selectedSplitByPath.field) : undefined;
    const aggregationSplitByOptions = validAggregationByType(splitByField?.type, splitByField?.typeItems);

    var splitByPathTree: TreeDataItem | null = null;
    if (selectedObject) {
        splitByPathTree = buildPathTree(objectInfos, sharedObjectInfos, selectedObject.object._key, onSplitByChange);
    }

    const categoryField = selectedCategoryPath?.field ? fieldByKeys?.get(selectedCategoryPath.field) : undefined;
    const aggregationCategoryOptions = validAggregationByType(categoryField?.type, categoryField?.typeItems);

    var categoryPathTree: TreeDataItem | null = null;
    if (selectedObject) {
        categoryPathTree = buildPathTree(objectInfos, sharedObjectInfos, selectedObject.object._key, onCategoryChange);
    }

    const separatorStyle = {
        backgroundColor: wsColor,
        opacity: 0.6
    }

    const onShowEmptyCategoriesChange = (value: boolean) => {
        setSelectedShowEmptyCategories(value);
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, value, selectedFilter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }

    function onFilterChange(filter: Filter | null) {
        setSelectedFilter(filter);
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedSplitByPath, selectedSplitByAggregation, selectedSplitBySortType, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedShowDots, selectedNullsAsZeros, selectedCategoryByYear, selectedLineStyle, selectedShowEmptyCategories, filter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }

    return (
        <>
            <Label className={TW_WidgetSectionLabel}>{t("dashboard.configuration.component.label")}</Label>
            <div className={TW_WidgetSpecificSection}>
                <Label>{t("dashboard.value.field.label")}</Label>
                {!selectedField && valueFieldTree && valueFieldTree.length > 0 && (
                    <TreeView searchable data={valueFieldTree} className={TW_DashboardCardTreeView} />
                )}
                {!selectedField && (!valueFieldTree || valueFieldTree.length === 0) && (
                    <span className={TW_DashboardCardFieldText}>{t("dashboard.value.field.no.objects")}</span>
                )}
                {!selectedField && sharedObjectTree && sharedObjectTree.length > 0 && (
                    <Collapsible open={isSharedObjectsOpen} onOpenChange={setIsSharedObjectsOpen} className="w-full">
                        <CollapsibleTrigger className={TW_SharedObjectsCollapsibleTrigger}>
                            <p className={TW_DashboardSection_Label}>{t(`dashboard.value.field.shared.objects.label`)}</p>
                            {isSharedObjectsOpen ? <ChevronDown className={TW_DashboardSection_Arrow} /> : <ChevronRight className={TW_DashboardSection_Arrow} />}
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <ScrollArea className={TW_DashboardSection_ScrollArea}>
                                <TreeView searchable data={sharedObjectTree} className={TW_DashboardCardTreeView} />
                            </ScrollArea>
                        </CollapsibleContent>
                    </Collapsible>
                )}
                {selectedField && selectedObject &&
                    <Command className={TW_DashboardPathSelector}>
                        <CommandItem
                            className={TW_DashboardCardFieldSelected}
                            onSelect={() => onFieldChange(undefined, undefined)}>
                            <span className={TW_DashboardCardFieldText}>{selectedObject?.object.name + " > " + selectedField.name}</span>
                            <ChevronDown className={TW_DashboardCardFieldArrow} />
                        </CommandItem>
                    </Command>
                }
                {selectedField && aggregationFieldOptions.length > 0 && (
                    <>
                        <Label>{t("dashboard.value.aggregation.label")}</Label>
                        <Select value={selectedFieldAggregation?.toString()} onValueChange={onFieldAggregationChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.value.aggregation.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                {aggregationFieldOptions.map(option => (
                                    <SelectItem key={option.toString()} value={option.toString()}>{t(`field.aggregation.label.${option}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                )}

                {selectedField && splitByPathTree &&
                    <>
                        <Separator orientation="horizontal" className="w-3/4 my-4 mx-auto" style={separatorStyle} />
                        <Label>{t("dashboard.splitBy.field.label")}</Label>
                        {!selectedSplitByPath && <TreeView searchable data={splitByPathTree} className={TW_DashboardCardTreeView} />}
                        {selectedSplitByPath &&
                            <Command className={TW_DashboardPathSelector}>
                                <CommandItem
                                    className={TW_DashboardCardFieldSelected}
                                    onSelect={() => onSplitByChange(undefined)}>
                                    <span className={TW_DashboardCardFieldText}>{selectedSplitByName}</span>
                                    <ChevronDown className={TW_DashboardCardFieldArrow} />
                                </CommandItem>
                            </Command>
                        }
                    </>
                }
                {selectedSplitByPath && splitByPathTree && aggregationSplitByOptions.length > 0 && (
                    <>
                        <Label>{t("dashboard.splitBy.aggregation.label")}</Label>
                        <Select value={selectedSplitByAggregation?.toString()} onValueChange={onSplitByAggregationChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.splitBy.aggregation.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                {aggregationSplitByOptions.map(option => (
                                    <SelectItem key={option.toString()} value={option.toString()}>{t(`field.aggregation.label.${option}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                )}
                {selectedSplitByPath && splitByPathTree && (
                    <>
                        <Label>{t("dashboard.splitBy.sort.label")}</Label>
                        <Select value={selectedSplitBySortType?.toString() ?? NONE.toString()} onValueChange={onSplitBySortTypeChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.splitBy.sort.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem key={NONE.toString()} value={NONE.toString()}>{t("sorting.type.name.NONE")}</SelectItem>
                                <SelectItem key={ASC.toString()} value={ASC.toString()}>{t("sorting.type.name.ASC")}</SelectItem>
                                <SelectItem key={DESC.toString()} value={DESC.toString()}>{t("sorting.type.name.DESC")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </>
                )}

                {selectedSplitByPath && selectedSplitByAggregation != null && 
                    [AggregationType.DAY, AggregationType.WEEK, AggregationType.MONTH, AggregationType.QUARTER].includes(selectedSplitByAggregation) && (
                    <>
                        <Separator orientation="horizontal" className="w-3/4 my-4 mx-auto" style={separatorStyle} />
                        <Label>{t("dashboard.category.field.label")}</Label>
                        <RadioGroup
                            value={selectedCategoryByYear ? "year" : "category"}
                            onValueChange={(v) => onCategoryByYearChange(v === "year")}
                            className="flex flex-col gap-2 w-full"
                        >
                            <div className="flex items-center gap-2">
                                <RadioGroupItem value="year" id="mode-year" />
                                <Label htmlFor="mode-year">{t("dashboard.groupby.year.label")}</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <RadioGroupItem value="category" id="mode-category" />
                                <Label htmlFor="mode-category">{t("dashboard.groupby.category.label")}</Label>
                            </div>
                        </RadioGroup>
                        {categoryPathTree && !selectedCategoryByYear && !selectedCategoryPath && <TreeView searchable data={categoryPathTree} className={TW_DashboardCardTreeView + " mt-2"} />}
                        {categoryPathTree && !selectedCategoryByYear && selectedCategoryPath &&
                            <Command className={TW_DashboardPathSelector + " mt-2"}>
                                <CommandItem
                                    className={TW_DashboardCardFieldSelected}
                                    onSelect={() => onCategoryChange(undefined)}>
                                    <span className={TW_DashboardCardFieldText}>{selectedCategoryName}</span>
                                    <ChevronDown className={TW_DashboardCardFieldArrow} />
                                </CommandItem>
                            </Command>
                        }
                    </>
                )}
                {selectedSplitByPath && categoryPathTree && (selectedSplitByAggregation == null || ![AggregationType.DAY, AggregationType.WEEK, AggregationType.MONTH, AggregationType.QUARTER].includes(selectedSplitByAggregation)) &&
                    <>
                        <Separator orientation="horizontal" className="w-3/4 my-4 mx-auto" style={separatorStyle} />
                        <Label>{t("dashboard.category.field.label")}</Label>
                        {!selectedCategoryPath && <TreeView searchable data={categoryPathTree} className={TW_DashboardCardTreeView} />}
                        {selectedCategoryPath &&
                            <Command className={TW_DashboardPathSelector}>
                                <CommandItem
                                    className={TW_DashboardCardFieldSelected}
                                    onSelect={() => onCategoryChange(undefined)}>
                                    <span className={TW_DashboardCardFieldText}>{selectedCategoryName}</span>
                                    <ChevronDown className={TW_DashboardCardFieldArrow} />
                                </CommandItem>
                            </Command>
                        }
                    </>
                }
                {selectedCategoryPath && !selectedCategoryByYear && aggregationCategoryOptions.length > 0 && (
                    <>
                        <Label>{t("dashboard.category.aggregation.label")}</Label>
                        <Select value={selectedCategoryAggregation?.toString()} onValueChange={onCategoryAggregationChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.category.aggregation.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                {aggregationCategoryOptions.map(option => (
                                    <SelectItem key={option.toString()} value={option.toString()}>{t(`field.aggregation.label.${option}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                )}
                {selectedCategoryPath && !selectedCategoryByYear && (
                    <>
                        <Label>{t("dashboard.category.sort.label")}</Label>
                        <Select value={selectedCategorySortType?.toString() ?? NONE.toString()} onValueChange={onCategorySortTypeChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.category.sort.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem key={NONE.toString()} value={NONE.toString()}>{t("sorting.type.name.NONE")}</SelectItem>
                                <SelectItem key={ASC.toString()} value={ASC.toString()}>{t("sorting.type.name.ASC")}</SelectItem>
                                <SelectItem key={DESC.toString()} value={DESC.toString()}>{t("sorting.type.name.DESC")}</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="flex flex-row justify-start items-center gap-2 w-full py-1">
                            <Label htmlFor="show-empty-categories">{t("dashboard.category.show.empty.label")}</Label>
                            <Switch id="show-empty-categories" checked={selectedShowEmptyCategories} onCheckedChange={onShowEmptyCategoriesChange} style={{ backgroundColor: (selectedShowEmptyCategories ? wsColor : GRAY_300_COLOR) }} />
                        </div>
                    </>
                )}
                {selectedField && selectedObject &&
                            <>
                        <Separator orientation="horizontal" className="w-3/4 my-4 mx-auto" style={separatorStyle} />
                        <div className={TW_WidgetSpecificSection}>
                            <Label className={TW_WidgetSectionLabel}>{t("dashboard.configuration.filter.label")}</Label>
                            <WidgetFilterEditor widget={initialWidget} srcObjectKey={selectedObject.object._key} onFilterChange={onFilterChange} fieldAggregationByPathKeys={fieldAggregationByPathKeys} />
                        </div>
                    </>
                }
                <div className="flex flex-col justify-start items-start gap-2 w-full">
                    <Label className={TW_WidgetSectionLabel}>{t("dashboard.style.label")}</Label>
                    <div className="flex flex-row justify-start items-center gap-1 w-full">
                        <Label className="w-1/3 overflow-hidden text-ellipsis whitespace-nowrap">{t("dashboard.line.type.label")}</Label>
                        <Select value={selectedLineStyle.toString()} onValueChange={onLineStyleChange}>
                            <SelectTrigger className="w-2/3">
                                <SelectValue placeholder={t("dashboard.line.type.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.values(LineStyle).map(option => (
                                    <SelectItem key={option.toString()} value={option.toString()}>{t(`dashboard.line.type.${option}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex flex-row justify-start items-center gap-1 w-full py-1">
                        <Label className="w-1/3 overflow-hidden text-ellipsis whitespace-nowrap">{t("dashboard.line.show.dots.label")}</Label>
                        <Switch id="show-dots" checked={selectedShowDots} onCheckedChange={onShowDotsChange} style={{ backgroundColor: (selectedShowDots ? wsColor : GRAY_300_COLOR) }} />
                    </div>
                    <div className="flex flex-row justify-start items-center gap-1 w-full py-1">
                        <Label className="w-1/3 overflow-hidden text-ellipsis whitespace-nowrap">{t("dashboard.line.nulls.as.zeros.label")}</Label>
                        <Switch id="show-dots" checked={selectedNullsAsZeros} onCheckedChange={onNullsAsZerosChange} style={{ backgroundColor: (selectedNullsAsZeros ? wsColor : GRAY_300_COLOR) }} />
                    </div>
                </div>
            </div>
        </>
    );
}